import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Badge,
  Button,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";

import ImageGalleryModal from "./ImageGalleryModal";

export default function CustomerDetailPage({ apiBase = "/api" }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);

  // Fetch customer
  const fetchCustomer = async () => {
    try {
      const res = await fetch(`${apiBase}/customers/${id}/`);
      const data = await res.json();

      if (!res.ok) throw new Error("Failed to load");

      setCustomer(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  // OPEN IMAGE MODAL
  const openGallery = (images, index) => {
    setGalleryImages(images);
    setGalleryIndex(index);
    setShowGallery(true);
  };

  // DELETE IMAGE
  const handleDeleteImage = async (url) => {
    const confirmDelete = window.confirm("Delete this image?");
    if (!confirmDelete) return;

    try {
      await fetch(`${apiBase}/images/delete/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url }),
      });

      // refresh data
      fetchCustomer();
      setShowGallery(false);
    } catch (err) {
      console.error(err);
    }
  };

  // DELETE SESSION
  const handleDeleteSession = async (sessionId) => {
    const confirmDelete = window.confirm(
      "Delete this entire session and all images?",
    );

    if (!confirmDelete) return;

    try {
      const res = await fetch(`${apiBase}/sessions/${sessionId}/delete/`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Delete failed");

      fetchCustomer();
    } catch (err) {
      console.error(err);
      alert("Failed to delete session");
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner />
      </div>
    );
  }

  if (!customer) {
    return <div className="mt-4 text-center">Customer not found</div>;
  }

  return (
    <Container className="mt-4">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4>{customer.name}</h4>
          <div className="text-muted small">
            {customer.sessions.length} sessions
          </div>
        </div>

        <Button
          variant="dark"
          onClick={() => navigate(`/design?customer=${customer.id}`)}
        >
          + New Design
        </Button>
      </div>

      {/* EMPTY */}
      {customer.sessions.length === 0 && (
        <Card className="text-center p-4">
          <div className="text-muted">No sessions yet. Start designing!</div>
        </Card>
      )}

      {/* SESSIONS */}
      <Row className="g-3">
        {customer.sessions.map((session) => {
          const images = session.messages
            .flatMap((m) => m.images || [])
            .map((img) => img.url);

          const lastUserMessage = [...session.messages]
            .reverse()
            .find((m) => m.role === "user");

          return (
            <Col md={6} key={session.id}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between">
                    <Badge bg="dark">Session #{session.id}</Badge>

                    <small className="text-muted">
                      {new Date(session.created_at).toLocaleDateString()}
                    </small>
                  </div>

                  {session.title && (
                    <div className="fw-semibold mt-2">{session.title}</div>
                  )}

                  {lastUserMessage && (
                    <div className="text-muted small mt-2">
                      {lastUserMessage.content.slice(0, 80)}...
                    </div>
                  )}

                  {/* IMAGES */}
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {images.length > 0 ? (
                      images.slice(0, 6).map((img, i) => (
                        <img
                          key={i}
                          src={img}
                          alt=""
                          onClick={() => openGallery(images, i)}
                          style={{
                            width: 70,
                            height: 70,
                            objectFit: "cover",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        />
                      ))
                    ) : (
                      <div className="text-muted small">No images yet</div>
                    )}
                  </div>
                </Card.Body>

                <Card.Footer className="d-flex gap-2">
                  <Button
                    variant="outline-dark"
                    size="sm"
                    className="w-100"
                    onClick={() =>
                      navigate(
                        `/design?customer=${customer.id}&session=${session.id}`,
                      )
                    }
                  >
                    Open
                  </Button>

                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="w-100"
                    onClick={() => handleDeleteSession(session.id)}
                  >
                    Delete
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* IMAGE MODAL */}
      <ImageGalleryModal
        show={showGallery}
        images={galleryImages}
        startIndex={galleryIndex}
        onClose={() => setShowGallery(false)}
        onDelete={handleDeleteImage}
      />
    </Container>
  );
}
